import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';
import FeeDetailDialog from './FeeDetailDialog';
import CollectFeeDialog from './CollectFeeDialog';
import FeeReceiptPrint from './FeeReceiptPrint';
import { apiBase } from '../../utils/config';
import moment from 'moment';
import './FeeCollection.css';

const FeeCollection = () => {
    const { catchResponse } = useCatchResponse();
    const [studentList, setStudentList] = useState([]);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState("");
    const [schoolId, setSchoolId] = useState("");
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [classList, setClassList] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [financialYear, setFinancialYear] = useState('');
    const [showFeeDetail, setShowFeeDetail] = useState(false);
    const [showCollectFee, setShowCollectFee] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedAllocations, setSelectedAllocations] = useState([]);
    const [showReceipt, setShowReceipt] = useState(false);
    const [receiptPaymentId, setReceiptPaymentId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [showApprovals, setShowApprovals] = useState(false);
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [pageAccess, setPageAccess] = useState({});

    useEffect(() => {
        const init = async () => {
            const loginData = CommonConfig.loginData();
            const userId = loginData?._id;
            const schoolid = CommonConfig.isOrgOwner(loginData?.UserRole) ? loginData?._id : loginData?.CreatedBy;
            setSecurityUserID(userId);
            setSchoolId(schoolid);

            const accessData = CommonConfig.getUserAccess('Fee Collection');
            setPageAccess(accessData);

            const years = await CommonConfig.getFinancialYears(schoolid);
            const currentYear = CommonConfig.getCurrentFinancialYear(years);
            const selectedAcYear = CommonConfig.getCookie('selectedAcademicYear');
            const fy = selectedAcYear ?? currentYear?.value;
            setFinancialYear(fy);
            await GetClassList(userId, fy);
        };
        init();
    }, []);

    const GetClassList = async (id, fy) => {
        try {
            let data = { UserID: id, FinancialYearId: fy };
            const response = await api.post(APIConstant.path.getClassList, data);
            if (response.success) {
                const classes = Array.from(
                    new Map(
                        response.data?.filter(x => x?.Status === 'Active')?.map(x => [
                            x?.ClassId,
                            { value: x?.ClassId, label: `${x?.ClassName}-${x?.MediumName}-${x?.Board}` }
                        ])
                    ).values()
                );
                setClassList(classes);
                if (classes.length > 0) {
                    setSelectedClass(classes[0].value);
                    GetStudentList(classes[0].value, fy);
                }
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const GetStudentList = async (classId, fy) => {
        try {
            Loader.show();
            let data = { SchoolId: schoolId || securityUserID, ClassId: classId, FinancialYearId: fy || financialYear };
            const response = await api.post(APIConstant.path.GetStudentFeeCollectionList, data);
            if (response.success) {
                setStudentList(response.data || []);
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const onPageChange = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    const handleClassChange = (value) => {
        setSelectedClass(value);
        GetStudentList(value, financialYear);
    };

    const handleCollect = (student) => {
        setSelectedStudent(student);
        setShowFeeDetail(true);
    };

    const handleProceedToCollect = (allocations) => {
        setSelectedAllocations(allocations);
        setShowFeeDetail(false);
        setShowCollectFee(true);
    };

    const handleCollectDone = (paymentId) => {
        setShowCollectFee(false);
        setSelectedAllocations([]);
        if (paymentId) {
            setReceiptPaymentId(paymentId);
            setShowReceipt(true);
        } else {
            setSelectedStudent(null);
        }
        GetStudentList(selectedClass, financialYear);
    };

    const handleReceiptClose = () => {
        setShowReceipt(false);
        setReceiptPaymentId(null);
        setSelectedStudent(null);
    };

    const handleShowHistory = async (student) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetStudentPaymentHistory, {
                StudentId: student._id,
                SchoolId: schoolId || securityUserID,
                FinancialYearId: financialYear,
            });
            if (response.success) {
                setPaymentHistory(response.data || []);
                setSelectedStudent(student);
                setShowHistory(true);
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const handlePrintFromHistory = (paymentId) => {
        setShowHistory(false);
        setReceiptPaymentId(paymentId);
        setShowReceipt(true);
    };

    const fetchPendingApprovals = async () => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetPendingApprovals, {
                SchoolId: schoolId || securityUserID,
                FinancialYearId: financialYear,
            });
            if (response.success) {
                setPendingApprovals(response.data || []);
                setShowApprovals(true);
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const handleApprove = async (paymentId) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.ApproveManualPayment, {
                PaymentId: paymentId,
                ApprovedBy: securityUserID,
            });
            if (response.success) {
                Toast.success({ message: response.message });
                fetchPendingApprovals();
                GetStudentList(selectedClass, financialYear);
            } else {
                Toast.error({ message: response.message });
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const handleReject = async (paymentId) => {
        const reason = prompt('Enter rejection reason:');
        if (reason === null) return;
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.RejectManualPayment, {
                PaymentId: paymentId,
                RejectedBy: securityUserID,
                RejectionReason: reason,
            });
            if (response.success) {
                Toast.success({ message: response.message });
                fetchPendingApprovals();
            } else {
                Toast.error({ message: response.message });
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const actionBodyTemplateSrNo = (data, props) => props.rowIndex + 1;

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.Status === 'Paid' ? 'success' : rowData.Status === 'No Fees' ? 'info' : 'danger';
        return <Tag value={rowData.Status} severity={severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '6px' }}>
                <Button
                    icon="pi pi-wallet"
                    tooltip="Collect"
                    tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    className="p-button-sm p-button-outlined fc-action-btn"
                    onClick={() => handleCollect(rowData)}
                    disabled={rowData.Status === 'No Fees' || rowData.Status === 'Paid'}
                />
                {rowData.TotalPaid > 0 && (
                    <Button
                        icon="pi pi-history"
                        tooltip="History"
                        tooltipOptions={{ position: 'top' }}
                        className="p-button-sm p-button-outlined p-button-secondary fc-action-btn"
                        onClick={() => handleShowHistory(rowData)}
                    />
                )}
            </div>
        );
    };

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-wallet"></i>
                    <h2>Fee Collection</h2>
                </div>
                <div className="p-d-flex p-ai-center">
                    <Button
                        icon="pi pi-check-square"
                        label="Pending Approvals"
                        className="p-button-sm p-button-warning"
                        onClick={fetchPendingApprovals}
                        style={{ height: '36px' }}
                    />
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Global Search" />
                    </div>
                    <Dropdown
                        value={selectedClass}
                        options={classList}
                        onChange={(e) => handleClassChange(e.value)}
                        placeholder="Select Class"
                        filter
                        showClear
                        style={{ minWidth: '200px', height: '36px' }}
                    />
                </div>
            </div>

            <DataTable
                value={studentList}
                paginator
                rows={rows}
                first={first}
                filters={filters}
                filterDisplay="menu"
                responsiveLayout="scroll"
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[10, 20, 50]}
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                onPage={onPageChange}
                className='custom-table'
                scrollHeight='calc(100vh - 120px)'
                size='small'
            >
                <Column body={actionBodyTemplateSrNo} header="Sr No." style={{ width: '8%' }} />
                <Column field="GRNo" header="G.R. No." style={{ width: '12%' }} />
                <Column field="StudentName" header="Student Name" style={{ width: '22%' }} />
                <Column field="RollNo" header="Roll No." style={{ width: '10%' }} />
                <Column field="ContactNo" header="Contact No." style={{ width: '18%' }} />
                <Column body={statusBodyTemplate} header="Status" style={{ width: '12%' }} />
                <Column body={actionBodyTemplate} header="Action" style={{ width: '18%' }} />
            </DataTable>

            <Dialog
                visible={showFeeDetail}
                onHide={() => setShowFeeDetail(false)}
                header="Student Fee Detail"
                style={{ width: '80vw', maxWidth: '900px' }}
                draggable={false}
                dismissableMask
            >
                {selectedStudent && (
                    <FeeDetailDialog
                        student={selectedStudent}
                        schoolId={schoolId || securityUserID}
                        financialYear={financialYear}
                        onProceedToCollect={handleProceedToCollect}
                        onClose={() => setShowFeeDetail(false)}
                    />
                )}
            </Dialog>

            <Dialog
                visible={showCollectFee}
                onHide={() => setShowCollectFee(false)}
                header="Collect Fees"
                style={{ width: '80vw', maxWidth: '900px' }}
                draggable={false}
                dismissableMask
            >
                {selectedStudent && (
                    <CollectFeeDialog
                        student={selectedStudent}
                        allocations={selectedAllocations}
                        schoolId={schoolId || securityUserID}
                        financialYear={financialYear}
                        userId={securityUserID}
                        onDone={handleCollectDone}
                        onClose={() => setShowCollectFee(false)}
                    />
                )}
            </Dialog>

            <Dialog
                visible={showHistory}
                onHide={() => setShowHistory(false)}
                header={`Payment History - ${selectedStudent?.StudentName || ''}`}
                style={{ width: '70vw', maxWidth: '800px' }}
                draggable={false}
                dismissableMask
            >
                <DataTable value={paymentHistory} size="small" className="custom-table" emptyMessage="No payments found">
                    <Column header="Sr No." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '8%' }} />
                    <Column field="ReceiptNo" header="Receipt No." style={{ width: '15%' }} />
                    <Column header="Date" body={(row) => moment(row.PaymentDate).format('DD/MM/YYYY')} style={{ width: '15%' }} />
                    <Column field="PaymentMode" header="Mode" style={{ width: '12%' }} />
                    <Column header="Amount" body={(row) => `₹ ${(row.NetAmount || 0).toLocaleString('en-IN')}`} style={{ width: '18%' }} />
                    <Column header="Discount" body={(row) => row.DiscountPercent ? `${row.DiscountPercent}%` : '-'} style={{ width: '12%' }} />
                    <Column header="Action" body={(row) => (
                        <Button
                            icon="pi pi-print"
                            tooltip="Print"
                            tooltipOptions={{ position: 'top' }}
                            className="e-sction-btn p-m-0"
                            onClick={() => handlePrintFromHistory(row._id)}
                        />
                    )} style={{ width: '20%' }} />
                </DataTable>
            </Dialog>

            <Dialog
                visible={showReceipt}
                onHide={handleReceiptClose}
                header="Fee Receipt"
                style={{ width: '85vw', maxWidth: '900px' }}
                draggable={false}
                dismissableMask
            >
                {receiptPaymentId && (
                    <FeeReceiptPrint
                        paymentId={receiptPaymentId}
                        schoolId={schoolId || securityUserID}
                        onClose={handleReceiptClose}
                    />
                )}
            </Dialog>

            <Dialog
                visible={showApprovals}
                onHide={() => setShowApprovals(false)}
                header={`Pending Approvals (${pendingApprovals.length})`}
                // style={{ width: '85vw', height: '90vh' }}
                className=""
                draggable={false}
                dismissableMask
            >
                <DataTable value={pendingApprovals} size="small" className="custom-table"
                    emptyMessage={<div style={{ position: 'absolute', left: 0, right: 0, top: '45%', textAlign: 'center', color: '#6c757d', fontWeight: '500' }}>No pending approvals</div>}
                    paginator rows={10} rowsPerPageOptions={[10, 20, 50]} scrollable scrollHeight="calc(100vh - 260px)">
                    <Column header="Sr No." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '6%' }} />
                    <Column field="StudentName" header="Student" style={{ width: '15%' }} />
                    <Column field="ReceiptNo" header="Receipt No." style={{ width: '10%' }} />
                    <Column header="Date" body={(row) => moment(row.PaymentDate).format('DD/MM/YYYY')} style={{ width: '10%' }} />
                    <Column header="Amount" body={(row) => `₹ ${(row.NetAmount || 0).toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                    <Column header="Proof" body={(row) => (
                        row.ManualProofImage ? (
                            <a href={`${apiBase}/Upload/PaymentProof/${row.ManualProofImage}?token=${CommonConfig.fileToken()}`} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#1C515E', fontWeight: 600 }}>
                                <i className="pi pi-image p-mr-1"></i> View
                            </a>
                        ) : '-'
                    )} style={{ width: '10%' }} />
                    <Column field="Remarks" header="Remarks" style={{ width: '15%' }} />
                    <Column header="Action" body={(row) => (
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <Button icon="pi pi-check" tooltip="Approve" tooltipOptions={{ position: 'top' }} className="p-button-sm p-button-success fc-action-btn"
                                onClick={() => handleApprove(row._id)} />
                            <Button icon="pi pi-times" tooltip="Reject" tooltipOptions={{ position: 'top' }} className="p-button-sm p-button-danger p-button-outlined fc-action-btn"
                                onClick={() => handleReject(row._id)} />
                        </div>
                    )} style={{ width: '22%' }} />
                </DataTable>
            </Dialog>
        </div>
    );
};

export default FeeCollection;
